import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Button, Paper, CircularProgress, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { API } from '../../utils/Axios.utils';

const PublicCandidatePortal = () => {
    const { token, action } = useParams(); // /offer/:action/:token
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const isDecline = action === 'decline';

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            // Check if it's a ZIP file
            if (!selectedFile.name.endsWith('.zip') && selectedFile.type !== 'application/zip') {
                setErrorMsg('Please upload a .zip file only.');
                setFile(null);
            } else {
                setErrorMsg('');
                setFile(selectedFile);
            }
        }
    };

    const handleSubmit = async () => {
        if (!isDecline && !file) {
            setErrorMsg('Please select a .zip file to upload.');
            return;
        }

        setLoading(true);
        setErrorMsg('');

        try {
            if (isDecline) {
                await API.post(`/api/offers/public/decline/${token}`);
                setSuccess(true);
            } else {
                const formData = new FormData();
                formData.append('document', file);
                
                await API.post(`/api/offers/public/accept/${token}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                setSuccess(true);
            }
        } catch (error) {
            console.error('Submission error:', error);
            setErrorMsg(error.response?.data?.error || 'An error occurred during submission.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f9fafb">
                <Paper sx={{ p: 5, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <CheckCircleIcon color={isDecline ? "warning" : "success"} sx={{ fontSize: 80, mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {isDecline ? 'Offer Declined' : 'Offer Accepted Successfully!'}
                    </Typography>
                    <Typography color="textSecondary">
                        {isDecline 
                            ? 'Thank you for your time. Your decision has been recorded.' 
                            : 'Thank you! Your signed offer letter and KYC documents have been securely uploaded and submitted to our HR team.'}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f9fafb">
            <Paper sx={{ p: 5, maxWidth: 600, width: '100%', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Box textAlign="center" mb={4}>
                    <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
                        {isDecline ? 'Decline Offer' : 'Accept Offer & Upload Documents'}
                    </Typography>
                    <Typography color="textSecondary">
                        {isDecline 
                            ? 'Are you sure you want to decline this offer?' 
                            : 'Please upload a single .zip file containing your signed offer letter and all required KYC documents.'}
                    </Typography>
                </Box>

                {errorMsg && <Alert severity="error" sx={{ mb: 3 }}>{errorMsg}</Alert>}

                {!isDecline && (
                    <Box 
                        sx={{ 
                            border: '2px dashed #ccc', 
                            borderRadius: 2, 
                            p: 4, 
                            textAlign: 'center',
                            bgcolor: '#fafafa',
                            mb: 4
                        }}
                    >
                        <input
                            accept=".zip,application/zip"
                            style={{ display: 'none' }}
                            id="zip-upload"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="zip-upload">
                            <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} size="large">
                                {file ? 'Change File' : 'Select .zip File'}
                            </Button>
                        </label>
                        {file && (
                            <Typography variant="body2" sx={{ mt: 2, color: 'success.main', fontWeight: 'bold' }}>
                                Selected: {file.name}
                            </Typography>
                        )}
                    </Box>
                )}

                <Box display="flex" justifyContent="center">
                    <Button 
                        variant="contained" 
                        color={isDecline ? "error" : "primary"}
                        size="large"
                        sx={{ px: 5, py: 1.5, borderRadius: 2 }}
                        onClick={handleSubmit}
                        disabled={loading || (!isDecline && !file)}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : (isDecline ? 'Confirm Decline' : 'Submit Documents')}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default PublicCandidatePortal;
